import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'editor/src/store/index';
import {
  getVariationSize,
  isSpreadEmpty,
  printsArea,
  SizeOption,
} from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import { Product, ProductVariation, Variant, VariationGroup } from 'editor/src/store/variants/types';

import { getCanonicalArea } from 'editor/src/util/reflectDesignData';

export type SizeSpecificSize = SizeOption & { isEmpty: boolean; hasDesign: boolean };

const getSizeOrder = (product: Product | undefined) => {
  const { sizeControlKey, productControls } = product ?? {};
  const control = sizeControlKey ? productControls?.find(({ key }) => key === sizeControlKey) : undefined;
  return control?.options.map(({ value }) => value) ?? [];
};

const sortSizes = <T extends SizeOption>(product: Product | undefined, sizes: T[]) => {
  const order = getSizeOrder(product);
  return sizes.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
};

const getSelectedGroup = (state: RootState) =>
  state.variants.variationGroups.find((group) => group.key === state.variants.selectedGroupKey);

/** The size whose artwork is on the canvas. */
export const getDisplayedSizeOption = (state: RootState): SizeOption | undefined => {
  const { product, displayedSize, selectedProductUid } = state.variants;
  const group = getSelectedGroup(state);
  const sizeOf = (variant: Variant | undefined) => variant && getVariationSize(variant.variation, product);
  const canvasUid = state.design.designData?.product_uid;
  return (
    sizeOf(group?.variationsInfo.find(({ variation }) => variation.productUid === canvasUid)) ??
    // A catalogue's design can name another uid than its variant, and a colour switch shows the old colour's design
    // until the new one's arrives: the size last put on the canvas holds then.
    group?.variationsInfo.map((variant) => sizeOf(variant)).find((size) => !!size && size.value === displayedSize) ??
    sizeOf(group?.variationsInfo.find(({ variation }) => variation.productUid === selectedProductUid)) ??
    sizeOf(group?.variationsInfo[0])
  );
};

export const getDisplayedSize = (state: RootState) => getDisplayedSizeOption(state)?.value;

/** The variant of `group` that shows `size`, preferring one that prints the area on the canvas. */
export const getSizeVariant = (state: RootState, group: VariationGroup, size: string | undefined) => {
  const sizeVariants = group.variationsInfo.filter(
    ({ variation, designData }) =>
      !!size && !!designData && getVariationSize(variation, state.variants.product)?.value === size,
  );
  // A size's variants may differ in print areas.
  const shownArea = getCanonicalArea(state.design.designData?.spreads[state.editor.currentSpreadIndex]?.name);
  return sizeVariants.find(({ designData }) => printsArea(designData, shownArea)) ?? sizeVariants[0];
};

const haveSameItems = (a: unknown, b: unknown) =>
  a === b ||
  (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => item === b[index]));

// Every canvas edit replaces the variants' designs but keeps their variations, so the sizes below are kept too.
const getProductVariations = createSelector(
  [(state: RootState) => state.variants.variationGroups],
  (variationGroups): ProductVariation[] =>
    variationGroups.flatMap((group) => group.variationsInfo.map(({ variation }) => variation)),
  { memoizeOptions: { resultEqualityCheck: haveSameItems } },
);

/** Every size selected for the product, in the size control's order. */
export const getProductSizes = createSelector(
  [getProductVariations, (state: RootState) => state.variants.product],
  (variations, product): SizeOption[] => {
    const sizes = new Map<string, SizeOption>();
    variations.forEach((variation) => {
      const size = getVariationSize(variation, product);
      if (size) {
        sizes.set(size.value, size);
      }
    });
    return sortSizes(product, [...sizes.values()]);
  },
);

/** The selected colour's sizes, each with whether it has artwork in the print area and a design to show. */
const getSizeSpecificSizes = (state: RootState, spreadName: string | undefined): SizeSpecificSize[] => {
  const group = getSelectedGroup(state);
  if (!group) {
    return [];
  }

  const area = getCanonicalArea(spreadName);
  const displayedSize = getDisplayedSize(state);
  const entries = new Map<string, { size: SizeSpecificSize; printsArea: boolean }>();
  group.variationsInfo.forEach(({ variation, designData }) => {
    const size = getVariationSize(variation, state.variants.product);
    const design = size?.value === displayedSize ? state.design.designData : designData;
    const spread = design?.spreads.find(({ name }) => getCanonicalArea(name) === area);
    const known = size && entries.get(size.value);
    // A size's variants may differ in print areas, so its entry comes from one that prints the area, if any does.
    if (!size || (known && (known.printsArea || (known.size.hasDesign && !spread)))) {
      return;
    }
    entries.set(size.value, {
      size: { ...size, isEmpty: !design || (!!spread && isSpreadEmpty(spread)), hasDesign: !!design },
      printsArea: !!spread,
    });
  });
  return sortSizes(
    state.variants.product,
    [...entries.values()].map(({ size }) => size),
  );
};

export default getSizeSpecificSizes;
